const FLUTTERWAVE_SCRIPT_URL = 'https://checkout.flutterwave.com/v3.js';

export const isFlutterwaveEnabled = (course) =>
  Boolean(course?.payment?.enabled && course?.payment?.provider === 'flutterwave');

const sanitizeAmount = (amount) => {
  const normalized = Number(amount);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

export const resolveCourseAmount = (course) => {
  const directAmount = sanitizeAmount(course?.payment?.amount);
  if (directAmount) return directAmount;

  const fallback = String(course?.price || '').replace(/[^0-9.]/g, '');
  const parsed = sanitizeAmount(fallback);
  return parsed || null;
};

export const loadFlutterwaveScript = () =>
  new Promise((resolve, reject) => {
    if (window.FlutterwaveCheckout) {
      resolve();
      return;
    }

    const existing = document.querySelector(`script[src="${FLUTTERWAVE_SCRIPT_URL}"]`);
    if (existing) {
      let attempts = 0;
      const maxAttempts = 40;
      const interval = window.setInterval(() => {
        if (window.FlutterwaveCheckout) {
          window.clearInterval(interval);
          resolve();
          return;
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
          window.clearInterval(interval);
          reject(new Error('Failed to initialize Flutterwave script'));
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = FLUTTERWAVE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Flutterwave script'));
    document.body.appendChild(script);
  });

export const buildFlutterwaveConfig = ({ course, customer }) => {
  const publicKey = process.env.REACT_APP_FLW_PUBLIC_KEY;
  const amount = resolveCourseAmount(course);

  if (!publicKey) {
    throw new Error('Missing Flutterwave public key. Set REACT_APP_FLW_PUBLIC_KEY.');
  }

  if (!amount) {
    throw new Error('Missing course amount. Set payment.amount in src/data/courses.json.');
  }

  const currency = course?.payment?.currency || process.env.REACT_APP_FLW_CURRENCY || 'USD';
  const paymentOptions =
    course?.payment?.paymentOptions || process.env.REACT_APP_FLW_PAYMENT_OPTIONS || 'card,account,banktransfer';
  const redirectUrl = process.env.REACT_APP_FLW_REDIRECT_URL || `${window.location.origin}${course.path || ''}`;
  const title = process.env.REACT_APP_FLW_TITLE || 'Vali Courses';
  const logo = process.env.REACT_APP_FLW_LOGO;
  const txRef = `valicourses_${course.id}_${Date.now()}`;

  return {
    public_key: publicKey,
    tx_ref: txRef,
    amount,
    currency,
    payment_options: paymentOptions,
    redirect_url: redirectUrl,
    customer: {
      email: customer.email,
      name: customer.name || 'Vali Courses Learner',
    },
    customizations: {
      title,
      description: `Payment for ${course.title}`,
      ...(logo ? { logo } : {}),
    },
    meta: {
      courseId: course.id,
      courseTitle: course.title,
    },
  };
};
